
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ReactNode } from "react";

interface AlertCardProps {
  title: string;
  description: string;
  icon: ReactNode;
  variant: "default" | "destructive" | "info" | "success" | "warning";
  action?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
}

const AlertCard = ({
  title,
  description,
  icon,
  variant = "default",
  action,
  className,
}: AlertCardProps) => {
  const variantClasses = {
    default: "border border-border bg-card",
    destructive: "border-destructive/50 text-destructive bg-destructive/10",
    info: "border-info/50 text-info bg-info/10",
    success: "border-success/50 text-success bg-success/10",
    warning: "border-warning/50 text-warning bg-warning/10",
  };
  
  return (
    <Alert
      className={cn(
        "transition-all duration-200",
        variantClasses[variant],
        className
      )}
    >
      <div className="flex items-start">
        <div className={cn(
          "mr-2",
          variant === "destructive" && "text-destructive",
          variant === "info" && "text-info",
          variant === "success" && "text-success",
          variant === "warning" && "text-warning",
        )}>
          {icon}
        </div>
        <div className="space-y-1 flex-1">
          <AlertTitle className="font-medium">{title}</AlertTitle>
          <AlertDescription className="text-sm">{description}</AlertDescription>
        </div>
        {action && (
          <Button 
            size="sm" 
            variant={variant === "default" ? "outline" : "secondary"}
            className={cn(
              "ml-2 whitespace-nowrap",
              variant === "destructive" && "hover:bg-destructive/20",
              variant === "info" && "hover:bg-info/20",
              variant === "success" && "hover:bg-success/20",
              variant === "warning" && "hover:bg-warning/20",
            )}
            onClick={action.onClick}
          >
            {action.label}
          </Button>
        )}
      </div>
    </Alert>
  );
};

export default AlertCard;
